// src/BiocharToSoil.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from './contexts/LanguageContext';
import { useAuth } from './AuthContext';
import KilnRegistration from './components/KilnRegistration';
import BurnLogger from './components/BurnLogger';
// import EarningsTracker from './components/EarningsTracker'; // Hidden per Michael's request

export default function BiocharToSoil() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [kilns, setKilns] = useState([]);
  const [burns, setBurns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showKilnForm, setShowKilnForm] = useState(false);
  const [showBurnForm, setShowBurnForm] = useState(false);
  const [selectedKiln, setSelectedKiln] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch kilns
      const kilnRes = await fetch(`${API_BASE}/api/kilns`, { headers });
      if (kilnRes.ok) {
        const kilnData = await kilnRes.json();
        setKilns(kilnData);
      }

      // Fetch burns
      const burnRes = await fetch(`${API_BASE}/api/burns`, { headers });
      if (burnRes.ok) {
        const burnData = await burnRes.json();
        setBurns(burnData);
      }

      // Calculate stats
      calculateStats();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const completedBurns = burns.filter(b => b.status === 'complete').length;
    const totalCredits = burns.reduce((sum, b) => sum + (b.credits_earned || 0), 0);
    const verifiedKilns = kilns.filter(k => k.status === 'verified').length;

    setStats({
      totalKilns: kilns.length,
      verifiedKilns,
      totalBurns: burns.length,
      completedBurns,
      totalCredits: totalCredits.toFixed(2)
      // estimatedEarnings removed - per Michael's request, no revenue promises on dashboard
    });
  };

  useEffect(() => {
    calculateStats();
  }, [kilns, burns]);

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      color: '#4ade80',
      fontSize: '24px',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#888',
      fontSize: '14px'
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    tab: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    tabActive: {
      background: '#4ade80',
      color: '#0f2419'
    },
    tabInactive: {
      background: '#1a3327',
      color: '#888',
      border: '1px solid #2d5a45'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      background: '#1a3327',
      padding: '20px',
      borderRadius: '12px',
      textAlign: 'center',
      border: '1px solid #2d5a45'
    },
    statNumber: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#4ade80',
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '12px',
      color: '#888',
      textTransform: 'uppercase'
    },
    card: {
      background: '#1a3327',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '16px',
      border: '1px solid #2d5a45'
    },
    cardTitle: {
      color: '#4ade80',
      fontSize: '18px',
      marginBottom: '16px'
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold'
    },
    buttonPrimary: {
      background: '#4ade80',
      color: '#0f2419'
    },
    buttonSecondary: {
      background: '#2d5a45',
      color: 'white'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '1px solid #2d5a45',
      color: '#888',
      fontSize: '12px',
      textTransform: 'uppercase'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #2d5a45',
      fontSize: '14px',
      color: 'white'
    },
    badge: {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 'bold'
    },
    badgeVerified: {
      background: '#22c55e',
      color: 'white'
    },
    badgePending: {
      background: '#f59e0b',
      color: 'white'
    },
    badgeComplete: {
      background: '#3b82f6',
      color: 'white'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#888'
    },
    actionRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '12px'
    }
  };

  // Earnings tab hidden per Michael's request - no revenue promises on dashboard
  const tabLabels = {
    overview: t('biocharToSoil.tabs.overview'),
    kilns: t('biocharToSoil.tabs.kilns'),
    burns: t('biocharToSoil.tabs.burns')
    // earnings: t('biocharToSoil.tabs.earnings') // Hidden
  };

  const OverviewTab = () => (
    <div>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.totalKilns || 0}</div>
          <div style={styles.statLabel}>{t('biocharToSoil.stats.totalKilns')}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.verifiedKilns || 0}</div>
          <div style={styles.statLabel}>{t('biocharToSoil.stats.verified')}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.completedBurns || 0}</div>
          <div style={styles.statLabel}>{t('biocharToSoil.stats.completedBurns')}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.totalCredits || 0}</div>
          <div style={styles.statLabel}>{t('biocharToSoil.stats.creditsEarned')}</div>
        </div>
        {/* Estimated Earnings card removed per Michael's request - no revenue promises on dashboard */}
      </div>

      {/* Quick Actions */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{t('biocharToSoil.quickActions.title')}</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button 
            style={{...styles.button, ...styles.buttonPrimary}}
            onClick={() => setShowKilnForm(true)}
          >
            {t('biocharToSoil.quickActions.registerKiln')}
          </button>
          <button 
            style={{...styles.button, ...styles.buttonSecondary}}
            onClick={() => {
              if (kilns.length > 0) {
                setSelectedKiln(kilns[0]);
                setShowBurnForm(true);
              } else {
                alert(t('biocharToSoil.burns.pleaseRegisterKiln'));
              }
            }}
          >
            {t('biocharToSoil.quickActions.logBurn')}
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{t('biocharToSoil.recentBurns.title')}</h3>
        {burns.length === 0 ? (
          <p style={{ color: '#888' }}>{t('biocharToSoil.recentBurns.noBurns')}</p>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {burns.slice(0, 3).map(burn => (
              <div key={burn.burn_id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '12px',
                background: '#0f2419',
                borderRadius: '8px'
              }}>
                <span style={{ color: 'white' }}>{burn.burn_date}</span>
                <span style={{ color: '#4ade80' }}>{burn.biomass_type}</span>
                <span style={{
                  ...styles.badge,
                  ...(burn.status === 'complete' ? styles.badgeComplete : styles.badgePending)
                }}>
                  {burn.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const KilnsTab = () => (
    <div>
      <div style={styles.actionRow}>
        <h3 style={{ color: 'white', margin: 0 }}>{t('biocharToSoil.kilns.yourKilns')}</h3>
        <button 
          style={{...styles.button, ...styles.buttonPrimary}}
          onClick={() => setShowKilnForm(true)}
        >
          {t('biocharToSoil.quickActions.registerKiln')}
        </button>
      </div>

      {kilns.length === 0 ? (
        <div style={{...styles.card, ...styles.emptyState}}>
          <p>{t('biocharToSoil.kilns.noKilns')}</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            {t('biocharToSoil.kilns.noKilnsDesc')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {kilns.map(kiln => (
            <div key={kiln.kiln_id} style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ color: '#4ade80', fontWeight: 'bold', marginBottom: '4px' }}>
                    {kiln.kiln_name || kiln.kiln_id}
                  </div>
                  <div style={{ color: '#888', fontSize: '13px' }}>
                    {t('biocharToSoil.kilns.registered')}: {kiln.created_at?.split('T')[0]}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    ...styles.badge,
                    ...(kiln.status === 'verified' ? styles.badgeVerified : styles.badgePending)
                  }}>
                    {kiln.status}
                  </span>
                  <button 
                    style={{...styles.button, ...styles.buttonSecondary, padding: '8px 16px'}}
                    onClick={() => {
                      setSelectedKiln(kiln);
                      setShowBurnForm(true);
                    }}
                  >
                    {t('biocharToSoil.kilns.logBurn')}
                  </button>
                </div>
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '12px',
                marginTop: '16px',
                padding: '16px',
                background: '#0f2419',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>{t('biocharToSoil.kilns.kilnGps')}</div>
                  <div style={{ color: 'white', fontSize: '13px', fontFamily: 'monospace' }}>
                    {kiln.gps_lat?.toFixed(4)}, {kiln.gps_lng?.toFixed(4)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>{t('biocharToSoil.kilns.farmGps')}</div>
                  <div style={{ color: 'white', fontSize: '13px', fontFamily: 'monospace' }}>
                    {kiln.farm_gps_lat?.toFixed(4)}, {kiln.farm_gps_lng?.toFixed(4)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>{t('biocharToSoil.kilns.totalBurns')}</div>
                  <div style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>
                    {burns.filter(b => b.kiln_id === kiln.kiln_id).length}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const BurnsTab = () => (
    <div>
      <div style={styles.actionRow}>
        <h3 style={{ color: 'white', margin: 0 }}>{t('biocharToSoil.burns.burnLogs')}</h3>
        <button 
          style={{...styles.button, ...styles.buttonPrimary}}
          onClick={() => {
            if (kilns.length > 0) {
              setSelectedKiln(kilns[0]);
              setShowBurnForm(true);
            } else {
              alert(t('biocharToSoil.burns.pleaseRegisterKiln'));
            }
          }}
          disabled={kilns.length === 0}
        >
          {t('biocharToSoil.quickActions.logBurn')}
        </button>
      </div>

      {burns.length === 0 ? (
        <div style={{...styles.card, ...styles.emptyState}}>
          <p>{t('biocharToSoil.burns.noBurns')}</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            {t('biocharToSoil.burns.noBurnsDesc')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {burns.map(burn => {
            const steps = JSON.parse(burn.steps_completed || '[]');
            return (
              <div key={burn.burn_id} style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ color: '#4ade80', fontWeight: 'bold', marginBottom: '4px' }}>
                      {burn.burn_id}
                    </div>
                    <div style={{ color: '#888', fontSize: '13px' }}>
                      {burn.burn_date} • {burn.kiln_id}
                    </div>
                  </div>
                  <span style={{
                    ...styles.badge,
                    ...(burn.status === 'complete' ? styles.badgeComplete : styles.badgePending)
                  }}>
                    {burn.status}
                  </span>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '16px',
                  padding: '16px',
                  background: '#0f2419',
                  borderRadius: '8px',
                  flexWrap: 'wrap'
                }}>
                  {[
                    { num: 1, name: t('biocharToSoil.burns.stepNames.biomass'), key: 'step1' },
                    { num: 2, name: t('biocharToSoil.burns.stepNames.burn'), key: 'step2' },
                    { num: 3, name: t('biocharToSoil.burns.stepNames.activate'), key: 'step3' },
                    { num: 4, name: t('biocharToSoil.burns.stepNames.soil'), key: 'step4' }
                  ].map(step => {
                    const completed = steps.includes(step.key);
                    return (
                      <div 
                        key={step.num}
                        style={{ 
                          flex: '1 1 100px',
                          textAlign: 'center',
                          opacity: completed ? 1 : 0.5
                        }}
                      >
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: completed ? '#4ade80' : '#2d5a45',
                          color: completed ? '#0f2419' : '#888',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                          fontWeight: 'bold'
                        }}>
                          {completed ? '✓' : step.num}
                        </div>
                        <div style={{ color: completed ? 'white' : '#888', fontSize: '12px' }}>
                          {step.name}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                  gap: '12px',
                  marginTop: '12px'
                }}>
                  <div>
                    <div style={{ color: '#888', fontSize: '11px' }}>{t('biocharToSoil.burns.details.biomass')}</div>
                    <div style={{ color: 'white', fontSize: '14px' }}>
                      {burn.biomass_quantity} {burn.biomass_unit}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '11px' }}>{t('biocharToSoil.burns.details.type')}</div>
                    <div style={{ color: 'white', fontSize: '14px' }}>{burn.biomass_type || '-'}</div>
                  </div>
                  {/* Credits display kept but earnings/revenue removed */}
                  <div>
                    <div style={{ color: '#888', fontSize: '11px' }}>{t('biocharToSoil.burns.details.credits')}</div>
                    <div style={{ color: '#4ade80', fontSize: '16px', fontWeight: 'bold' }}>
                      {burn.credits_earned?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>

                {burn.status !== 'complete' && (
                  <button 
                    style={{...styles.button, ...styles.buttonSecondary, marginTop: '12px', width: '100%'}}
                    onClick={() => {
                      setSelectedKiln(kilns.find(k => k.kiln_id === burn.kiln_id));
                      setShowBurnForm(true);
                    }}
                  >
                    {t('biocharToSoil.burns.continueLogging')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // EarningsTab removed per Michael's request - no revenue promises on dashboard

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t('biocharToSoil.title')}</h1>
        <p style={styles.subtitle}>
          {t('biocharToSoil.subtitle')}
        </p>
      </div>

      {/* Earnings tab removed from tabs - only showing overview, kilns, burns */}
      <div style={styles.tabs}>
        {['overview', 'kilns', 'burns'].map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : styles.tabInactive)
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          {t('common.loading')}
        </div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'kilns' && <KilnsTab />}
          {activeTab === 'burns' && <BurnsTab />}
          {/* EarningsTab removed */}
        </>
      )}

      {showKilnForm && (
        <KilnRegistration 
          onClose={() => setShowKilnForm(false)}
          onSuccess={() => {
            setShowKilnForm(false);
            fetchData();
          }}
        />
      )}

      {showBurnForm && selectedKiln && (
        <BurnLogger 
          kiln={selectedKiln}
          onClose={() => {
            setShowBurnForm(false);
            setSelectedKiln(null);
          }}
          onSuccess={() => {
            setShowBurnForm(false);
            setSelectedKiln(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}